import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPaperclip, faPaperPlane, faClockRotateLeft, faComments } from '@fortawesome/free-solid-svg-icons';
import { guestApi } from '../../utils/portalApi';
import Avatar from '../Avatar';
import RichText from '../RichText';
import TaskMediaGrid from '../TaskMediaGrid';
import { dueLabel, statusTone } from './GuestTasks';
import '../../styles/tasks.css';

/**
 * One of the guest's tasks (guest role, Phase 3): the brief, the status they
 * share with everyone on it, and its discussion.
 *
 * The status rules are the server's - proof when the assigner asked for it, a
 * note to finish without a file, a reason to put it on hold - so this page
 * just shows what the server says when a move is refused.
 */

const STATUSES = ['Pending', 'In Progress', 'On Hold', 'Completed'];

const when = (d) => new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const StatusForm = ({ task, onSaved }) => {
    const [status, setStatus] = useState(task.status);
    const [note, setNote] = useState('');
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);

    useEffect(() => { setStatus(task.status); }, [task.status]);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const body = new FormData();
            body.append('status', status);
            body.append('statusNote', note);
            files.forEach((f) => body.append('completionProof', f));
            const { data } = await guestApi.put(`/tasks/${task._id}/status`, body, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setNote('');
            setFiles([]);
            onSaved(data);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not update the status.');
        } finally {
            setBusy(false);
        }
    };

    const needsNote = status !== task.status && (status === 'On Hold' || (status === 'Completed' && !files.length));
    return (
        <form className="guest-card guest-status-form" onSubmit={save}>
            <h2>Update status</h2>
            <div className="guest-status-pick" role="radiogroup" aria-label="Status">
                {STATUSES.map((s) => (
                    <button key={s} type="button" role="radio" aria-checked={status === s}
                        className={`guest-status ${statusTone(s)} ${status === s ? 'picked' : ''}`}
                        onClick={() => setStatus(s)}>
                        {s}
                    </button>
                ))}
            </div>
            <label className="portal-label">
                {status === 'On Hold' ? 'Why is it on hold?' : 'Note (optional)'}
                <textarea className="ext-input" rows={3} value={note} maxLength={2000}
                    placeholder={status === 'Completed' ? 'What was done' : 'Anything the team should know'}
                    onChange={(e) => setNote(e.target.value)} />
            </label>
            {(status === 'Completed' || files.length > 0) && (
                <div className="guest-proof">
                    <button type="button" className="msgr-btn" onClick={() => fileRef.current?.click()}>
                        <FontAwesomeIcon icon={faPaperclip} /> {task.requiresAttachment ? 'Attach proof (required)' : 'Attach files'}
                    </button>
                    <input ref={fileRef} type="file" multiple hidden
                        onChange={(e) => { setFiles([...files, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ''; }} />
                    {files.map((f, i) => (
                        <span key={`${f.name}-${i}`} className="guest-file">
                            {f.name}
                            <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</button>
                        </span>
                    ))}
                </div>
            )}
            {needsNote && !note.trim() && (
                <p className="guest-muted">{status === 'On Hold' ? 'A reason is needed to put it on hold.' : 'Add a note or attach a file to mark it completed.'}</p>
            )}
            {error && <p className="portal-error">{error}</p>}
            <button className="portal-primary" disabled={busy || (status === task.status && !files.length && !note.trim())}>
                {busy ? 'Saving…' : 'Save'}
            </button>
        </form>
    );
};

const Discussion = ({ taskId }) => {
    const [comments, setComments] = useState(null);
    const [text, setText] = useState('');
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const listRef = useRef(null);

    const load = useCallback(() => guestApi.get(`/tasks/${taskId}/comments`)
        .then(({ data }) => setComments(data)).catch(() => setComments((c) => c || [])), [taskId]);

    useEffect(() => {
        load();
        const timer = setInterval(load, 15000);
        return () => clearInterval(timer);
    }, [load]);

    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [comments?.length]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() && !files.length) return;
        setBusy(true);
        setError('');
        try {
            const body = new FormData();
            body.append('message', text.trim());
            files.forEach((f) => body.append('attachments', f));
            const { data } = await guestApi.post(`/tasks/${taskId}/comments`, body, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setComments((prev) => [...(prev || []), data]);
            setText('');
            setFiles([]);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not send your message.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="guest-card">
            <h2><FontAwesomeIcon icon={faComments} /> Discussion</h2>
            <div className="guest-comments" ref={listRef}>
                {comments === null && <div className="portal-spinner" />}
                {comments && !comments.length && <p className="guest-muted">No messages yet. Ask a question or share an update.</p>}
                {(comments || []).map((c) => (
                    <div key={c._id} className={`guest-comment ${c.author.me ? 'mine' : ''}`}>
                        <Avatar name={c.author.name} profilePic={c.author.profilePic} className="msgr-avatar" />
                        <div>
                            <div className="guest-comment-head">
                                <strong>{c.author.me ? 'You' : c.author.name}</strong>
                                <span>{when(c.createdAt)}</span>
                            </div>
                            {c.replyTo && <blockquote>{c.replyTo.authorName}: {c.replyTo.text}</blockquote>}
                            {c.message && <p>{c.message}</p>}
                            <TaskMediaGrid media={c.attachments} />
                        </div>
                    </div>
                ))}
            </div>
            <form className="guest-composer" onSubmit={send}>
                <textarea className="ext-input" rows={2} value={text} maxLength={5000}
                    placeholder="Write a message to the team" onChange={(e) => setText(e.target.value)} />
                <div className="guest-composer-row">
                    <button type="button" className="msgr-btn" onClick={() => fileRef.current?.click()}>
                        <FontAwesomeIcon icon={faPaperclip} /> Attach
                    </button>
                    <input ref={fileRef} type="file" multiple hidden
                        onChange={(e) => { setFiles([...files, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ''; }} />
                    {files.map((f, i) => (
                        <span key={`${f.name}-${i}`} className="guest-file">
                            {f.name}
                            <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</button>
                        </span>
                    ))}
                    <button className="portal-primary guest-send" disabled={busy || (!text.trim() && !files.length)}>
                        <FontAwesomeIcon icon={faPaperPlane} /> {busy ? 'Sending…' : 'Send'}
                    </button>
                </div>
                {error && <p className="portal-error">{error}</p>}
            </form>
        </section>
    );
};

const GuestTaskDetail = ({ onAuthError, onChanged }) => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setTask(null);
        setError('');
        guestApi.get(`/tasks/${taskId}`).then(({ data }) => setTask(data)).catch((err) => {
            onAuthError(err);
            setError(err.response?.data?.message || 'This task is not available.');
        });
    }, [taskId, onAuthError]);

    if (error) {
        return (
            <main className="guest-page">
                <button type="button" className="guest-link" onClick={() => navigate('/guest/tasks')}><FontAwesomeIcon icon={faArrowLeft} /> All tasks</button>
                <p className="portal-error">{error}</p>
            </main>
        );
    }
    if (!task) return <main className="guest-page"><div className="portal-spinner" /></main>;

    const people = [
        ...task.assignees.map((p) => ({ ...p, label: p.name })),
        ...task.guests.map((g) => ({ ...g, label: g.me ? `${g.name} (you)` : g.name }))
    ];

    return (
        <main className="guest-page guest-task">
            <button type="button" className="guest-link" onClick={() => navigate('/guest/tasks')}>
                <FontAwesomeIcon icon={faArrowLeft} /> All tasks
            </button>

            <header className="guest-task-head">
                <h1>{task.title}</h1>
                <div className="guest-task-tags">
                    <span className={`guest-status ${statusTone(task.status)}`}>{task.status}</span>
                    <span className="guest-priority">{task.priority}</span>
                </div>
                <p className="guest-muted">
                    {task.projectName || 'Office task'}
                    {task.assignedBy?.name && ` · from ${task.assignedBy.name}`}
                    {' · '}{task.status === 'Completed' && task.completedAt ? `Completed ${when(task.completedAt)}` : dueLabel(task)}
                </p>
            </header>

            <div className="guest-task-grid">
                <div className="guest-task-main">
                    <section className="guest-card">
                        <h2>Brief</h2>
                        <RichText text={task.description} fallback={<p className="guest-muted">No description.</p>} />
                        <TaskMediaGrid media={task.attachments} />
                        {task.requiresAttachment && <p className="guest-muted">Proof is needed to mark this completed.</p>}
                    </section>

                    <StatusForm task={task} onSaved={(next) => { setTask(next); onChanged?.(); }} />

                    <Discussion taskId={task._id} />
                </div>

                <aside className="guest-task-side">
                    <section className="guest-card">
                        <h2>Working on this</h2>
                        <ul className="guest-people">
                            {people.map((p) => (
                                <li key={p._id}><Avatar name={p.name} profilePic={p.profilePic} className="msgr-avatar" /> {p.label}</li>
                            ))}
                        </ul>
                    </section>

                    {task.completionProof.length > 0 && (
                        <section className="guest-card">
                            <h2>Proof of work</h2>
                            <TaskMediaGrid media={task.completionProof} />
                        </section>
                    )}

                    <section className="guest-card">
                        <h2><FontAwesomeIcon icon={faClockRotateLeft} /> History</h2>
                        <ul className="guest-history">
                            {[...task.statusHistory].reverse().map((h, i) => (
                                <li key={`${h.at}-${i}`}>
                                    <strong>{h.to}</strong>{h.from ? ` from ${h.from}` : ''}
                                    <span>{when(h.at)}{h.byName ? ` · ${h.byName}` : ''}</span>
                                    {h.note && <em>{h.note}</em>}
                                </li>
                            ))}
                        </ul>
                    </section>
                </aside>
            </div>
        </main>
    );
};

export default GuestTaskDetail;
